import is from '@sindresorhus/is';
import { Fragment, type FunctionComponent, type ReactNode, useMemo, useRef } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { AssistantBubble } from '~/ui/domain/chat/AssistantBubble';
import { UserBubble } from '~/ui/domain/chat/UserBubble';

import type {
    AgentChatMessage,
    AgentChatMessagePage,
    AssistantMessageEntry,
    AwaitingCallback,
} from '../../data-contract/chat';
import {
    type BlockWithMeta,
    computeMessageBlocksMeta,
    renderBlocksForEvent,
    renderContextRefreshEntry,
} from '../block-renderers';
import { ChatMarkdown } from '../ChatMarkdown';
import { useScrollToBottom } from '../hooks/use-scroll-to-bottom';
import { containsMarkdownTable } from '../markdown-detection';
import { useChatStore } from '../stores/chat-open-store';

type ChatMessageListProps = {
    readonly awaitingCallback: AwaitingCallback | null;
    readonly isLoading: boolean;
    readonly pages: readonly AgentChatMessagePage[];
};

const getUserText = (message: AgentChatMessage): string => {
    if (is.nonEmptyString(message.text)) return message.text;
    if (is.nullOrUndefined(message.entries)) return '';
    return message.entries
        .filter((entry) => entry.type === 'text')
        .map((entry) => entry.text)
        .join('');
};

const AssistantTextEntryBubble: FunctionComponent<{
    readonly entry: Extract<AssistantMessageEntry, { type: 'text' }>;
    readonly index: number;
    readonly isLastStreaming: boolean;
}> = ({ entry, index, isLastStreaming }) => {
    const isMarkdownTableWide = useChatStore((state) => state.isMarkdownTableWide);
    const fullWidth = isMarkdownTableWide && containsMarkdownTable(entry.text);

    return (
        <AssistantBubble key={`text-${index}`}>
            <AssistantBubble.Text fullWidth={fullWidth} streaming={isLastStreaming}>
                <ChatMarkdown>{entry.text}</ChatMarkdown>
            </AssistantBubble.Text>
        </AssistantBubble>
    );
};

const renderEntry = (
    entry: AssistantMessageEntry,
    index: number,
    isLastStreaming: boolean,
    blocksMeta: readonly BlockWithMeta[],
    isTurnComplete: boolean,
): ReactNode => {
    if (entry.type === 'text') {
        if (entry.text.length === 0) return null;
        return (
            <AssistantTextEntryBubble
                key={`text-${index}`}
                entry={entry}
                index={index}
                isLastStreaming={isLastStreaming}
            />
        );
    }
    if (entry.type === 'context-refresh') {
        return renderContextRefreshEntry(entry);
    }
    return (
        <Fragment key={entry.envelope.eventId}>
            {renderBlocksForEvent(entry.envelope.eventId, blocksMeta, isTurnComplete)}
        </Fragment>
    );
};

const renderAssistantMessage = (
    message: AgentChatMessage,
    isLatestAssistant: boolean,
    awaitingCallback: AwaitingCallback | null,
    errorMessage: string | null,
): ReactNode => {
    if (!is.null(errorMessage)) {
        return (
            <AssistantBubble key={message.id}>
                <AssistantBubble.Text fullWidth={true}>
                    <span className="text-error-700">{errorMessage}</span>
                </AssistantBubble.Text>
            </AssistantBubble>
        );
    }

    const entries = message.entries ?? [];
    const isMessageStreaming = message.status === 'pending';

    if (entries.length === 0) return null;

    const blocksMeta = computeMessageBlocksMeta(message, isLatestAssistant, awaitingCallback);
    const lastIndex = entries.length - 1;
    const isTurnComplete = !isMessageStreaming;

    return (
        <Fragment key={message.id}>
            {entries.map((entry, index) =>
                renderEntry(entry, index, isMessageStreaming && index === lastIndex, blocksMeta, isTurnComplete),
            )}
        </Fragment>
    );
};

export const ChatMessageList: FunctionComponent<ChatMessageListProps> = ({ awaitingCallback, isLoading, pages }) => {
    const translate = useTranslate();
    const scrollRef = useRef<HTMLDivElement>(null);

    const messages = useMemo<readonly AgentChatMessage[]>(() => {
        const flattened = pages.flatMap((page) => page.messages);
        return [...flattened].reverse();
    }, [pages]);

    const latestAssistantId = useMemo<string | null>(() => {
        for (let index = messages.length - 1; index >= 0; index--) {
            const message = messages[index];
            if (!is.undefined(message) && message.role === 'assistant') return message.id;
        }
        return null;
    }, [messages]);

    useScrollToBottom(scrollRef, [messages]);

    return (
        <ScrollArea viewportRef={scrollRef} className="min-h-0 w-full flex-1">
            <div className="flex min-h-0 w-full flex-1 flex-col gap-l p-xxl py-l">
                {isLoading && messages.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center">
                        <Spinner className="h-l w-l" />
                    </div>
                ) : null}
                {messages.map((message) =>
                    message.role === 'user' ? (
                        <UserBubble key={message.id}>{getUserText(message)}</UserBubble>
                    ) : (
                        renderAssistantMessage(
                            message,
                            message.id === latestAssistantId,
                            awaitingCallback,
                            message.status === 'error' ? translate('chat.error-fallback') : null,
                        )
                    ),
                )}
            </div>
        </ScrollArea>
    );
};
